/**
 * UTILITAIRES DE CALCUL UNIFIÉS
 * Centralise toutes les logiques de calcul pour éviter les duplications
 * Créé pour remplacer les fonctions calculateDocumentTotals dispersées
 */

/**
 * Calcule les totaux d'un document avec support pour différents modes
 * @param {Array} items - Array des items du document
 * @param {Object} options - Options de calcul
 * @returns {Object} Totaux calculés
 */
export const calculateDocumentTotals = (items = [], options = {}) => {
  const {
    // Options pour les calculs avancés (DocumentFormWithPricing)
    appliquerTvaGlobale = true,
    appliquerRetenueForfaitaire = false,
    nbJoursImmobilisation = 0,
    montantJourImmobilisation = 0,
    montantPaye = 0,
    
    // Options pour les remises (DocumentForm)
    remiseType = null,
    remiseValue = 0,
    
    // Mode de calcul
    mode = 'advanced' // 'basic' | 'advanced'
  } = options;

  // Validation des données d'entrée
  if (!Array.isArray(items)) {
    console.warn('calculateDocumentTotals: items should be an array');
    items = [];
  }

  // === CALCULS DE BASE ===
  const totalTransporteurHt = items.reduce((sum, item) => {
    const tarif = parseFloat(item.tarif_transporteur_ht) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    return sum + (tarif * quantity);
  }, 0);

  const totalMargeHt = items.reduce((sum, item) => {
    const margeHt = parseFloat(item.marge_ht) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    return sum + (margeHt * quantity);
  }, 0);

  const totalHt = items.reduce((sum, item) => {
    return sum + (parseFloat(item.total_ht) || 0);
  }, 0);

  const totalTva = items.reduce((sum, item) => {
    const itemTva = parseFloat(item.total_tva) || 0;
    return sum + itemTva;
  }, 0);

  // === CALCULS AVANCÉS ===
  let fraisImmobilisation = 0;
  let tvaImmobilisation = 0;
  
  if (mode === 'advanced') {
    // Calcul automatique des frais d'immobilisation
    fraisImmobilisation = nbJoursImmobilisation * montantJourImmobilisation;
    
    // Retenue forfaitaire sur les frais d'immobilisation si activée
    if (appliquerRetenueForfaitaire && fraisImmobilisation > 0) {
      fraisImmobilisation = fraisImmobilisation / 0.98;
    }
    
    // TVA sur les frais d'immobilisation si la TVA globale est activée
    if (appliquerTvaGlobale && fraisImmobilisation > 0) {
      tvaImmobilisation = fraisImmobilisation * 0.18;
    }
  }

  // === CALCULS DE REMISES (MODE BASIC) ===
  let remiseMontant = 0;
  if (mode === 'basic' && remiseType && remiseValue > 0) {
    if (remiseType === 'pourcentage') {
      remiseMontant = (totalHt * remiseValue) / 100;
    } else {
      remiseMontant = remiseValue;
    }
  }

  // === TOTAUX FINAUX ===
  const totalHtWithImmobilisation = totalHt + fraisImmobilisation;
  const totalTvaWithImmobilisation = totalTva + tvaImmobilisation;
  const totalHtWithRemise = totalHt - remiseMontant;
  
  let finalTotalHt, finalTotalTva, finalTotalTtc;
  
  if (mode === 'advanced') {
    finalTotalHt = totalHtWithImmobilisation;
    finalTotalTva = appliquerTvaGlobale ? totalTvaWithImmobilisation : 0;
    finalTotalTtc = finalTotalHt + finalTotalTva;
  } else {
    finalTotalHt = totalHtWithRemise;
    finalTotalTva = totalTva;
    finalTotalTtc = finalTotalHt + finalTotalTva - remiseMontant;
  }

  const resteAPayer = finalTotalTtc - montantPaye;

  // === RETOUR UNIFIÉ ===
  return {
    // Totaux de base (compatibilité avec toutes les versions)
    totalHT: Math.round(finalTotalHt),
    totalTVA: Math.round(finalTotalTva),
    totalTTC: Math.round(finalTotalTtc),
    
    // Totaux avancés (DocumentFormWithPricing)
    total_transporteur_ht: Math.round(totalTransporteurHt),
    total_marge_ht: Math.round(totalMargeHt),
    total_ht: Math.round(finalTotalHt),
    total_tva: Math.round(finalTotalTva),
    total_ttc: Math.round(finalTotalTtc),
    reste_a_payer: Math.round(Math.max(0, resteAPayer)),
    frais_immobilisation_camion: fraisImmobilisation,
    
    // Totaux avec remises (DocumentForm)
    remiseMontant: Math.round(remiseMontant),
    totalApresRemiseHT: Math.round(totalHtWithRemise),
    totalApresRemiseTTC: Math.round(finalTotalTtc),
    
    // Métadonnées pour debug
    _meta: {
      mode,
      itemsCount: items.length,
      hasImmobilisation: fraisImmobilisation > 0,
      hasRemise: remiseMontant > 0,
      appliquerTvaGlobale
    }
  };
};

/**
 * Calcule le pricing d'un item individuel
 * @param {Object} item - Item à calculer
 * @param {Object} options - Options de calcul
 * @returns {Object} Item avec pricing calculé
 */
export const calculateItemPricing = (item, options = {}) => {
  const {
    changedField = null,
    appliquerTvaGlobale = true,
    appliquerRetenueForfaitaire = false
  } = options;

  // Copie de l'item pour éviter les mutations
  const calculatedItem = { ...item };
  
  // Conversion en nombres sécurisée
  const quantity = Math.max(0, parseFloat(calculatedItem.quantity) || 0);
  const tarifTransporteurHt = Math.max(0, parseFloat(calculatedItem.tarif_transporteur_ht) || 0);
  const margePourcentage = Math.max(0, parseFloat(calculatedItem.marge_pourcentage) || 0);
  const prixClientHt = Math.max(0, parseFloat(calculatedItem.prix_client_ht) || 0);
  const tvaRate = Math.max(0, parseFloat(calculatedItem.tva_rate) || 18);

  // === CALCUL SELON LE MODE ===
  if (calculatedItem.calcul_automatique !== false) {
    // MODE AUTOMATIQUE : tarif transporteur + marge % → prix client (markup on cost)
    // Formule correcte: Prix = Tarif × (1 + Marge%)
    calculatedItem.prix_client_ht = tarifTransporteurHt * (1 + margePourcentage / 100);
    calculatedItem.marge_ht = calculatedItem.prix_client_ht - tarifTransporteurHt;
    calculatedItem.unit_price = calculatedItem.prix_client_ht;
  } else {
    // MODE MANUEL : prix client + tarif transporteur → marge
    calculatedItem.marge_ht = Math.max(0, prixClientHt - tarifTransporteurHt);
    // Formule de marge % selon spécification: (P/C - 1) × 100
    calculatedItem.marge_pourcentage_calculee = tarifTransporteurHt > 0 ? 
      ((prixClientHt / tarifTransporteurHt - 1) * 100).toFixed(2) : 0;
    calculatedItem.unit_price = prixClientHt;
  }

  // === RETENUE FORFAITAIRE 2% ===
  // Si activée, majorer le prix client pour compenser la retenue
  if (appliquerRetenueForfaitaire) {
    calculatedItem.prix_client_ht = calculatedItem.prix_client_ht / 0.98;
    calculatedItem.unit_price = calculatedItem.prix_client_ht;
    
    // Recalculer la marge en FCFA si mode automatique
    if (calculatedItem.calcul_automatique !== false) {
      calculatedItem.marge_ht = calculatedItem.prix_client_ht - tarifTransporteurHt;
    }
  }

  // === CALCULS FINAUX ===
  calculatedItem.total_ht = calculatedItem.unit_price * quantity;
  
  // TVA selon la configuration globale et individuelle
  const appliquerTvaItem = calculatedItem.appliquer_tva !== false && appliquerTvaGlobale;
  calculatedItem.total_tva = appliquerTvaItem ? 
    (calculatedItem.total_ht * tvaRate / 100) : 0;
  
  calculatedItem.total_ttc = calculatedItem.total_ht + calculatedItem.total_tva;

  // Arrondir tous les montants
  ['marge_ht', 'prix_client_ht', 'unit_price', 'total_ht', 'total_tva', 'total_ttc']
    .forEach(field => {
      if (calculatedItem[field] !== undefined) {
        calculatedItem[field] = Math.round(calculatedItem[field]);
      }
    });

  return calculatedItem;
};

/**
 * Valide la cohérence des données d'un item
 * @param {Object} item - Item à valider
 * @returns {Object} Résultat de validation avec erreurs éventuelles
 */
export const validateItemData = (item) => {
  const errors = [];
  const warnings = [];

  // Validations critiques
  if (!item.description || item.description.trim() === '') {
    errors.push('La description est obligatoire');
  }

  if (!item.quantity || item.quantity <= 0) {
    errors.push('La quantité doit être supérieure à 0');
  }

  if (!item.tarif_transporteur_ht || item.tarif_transporteur_ht <= 0) {
    errors.push('Le tarif transporteur doit être supérieur à 0');
  }

  // Validations d'avertissement
  if (item.marge_pourcentage && item.marge_pourcentage < 0) {
    warnings.push('Marge négative détectée');
  }

  if (item.marge_pourcentage && item.marge_pourcentage > 50) {
    warnings.push('Marge très élevée (>50%)');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Formate un montant en FCFA
 * @param {number} amount - Montant à formatter
 * @returns {string} Montant formaté
 */
export const formatCurrency = (amount) => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0 FCFA';
  }
  // Format français avec espaces comme séparateurs de milliers
  return `${Math.round(amount).toLocaleString('fr-FR')} FCFA`;
};

/**
 * Calcule des statistiques sur les items d'un document
 * @param {Array} items - Items du document
 * @returns {Object} Statistiques calculées
 */
export const calculateDocumentStats = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      totalItems: 0,
      averageMargin: 0,
      totalWeight: 0,
      averageUnitPrice: 0
    };
  }

  const totalItems = items.length;
  const totalMargin = items.reduce((sum, item) => sum + (parseFloat(item.marge_pourcentage) || 0), 0);
  const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalUnitPrice = items.reduce((sum, item) => sum + (parseFloat(item.unit_price) || 0), 0);

  return {
    totalItems,
    averageMargin: totalItems > 0 ? (totalMargin / totalItems).toFixed(2) : 0,
    totalWeight,
    averageUnitPrice: totalItems > 0 ? (totalUnitPrice / totalItems) : 0
  };
};